import { apiClient } from '@/lib/api-client';

/**
 * The extra photos on a product page.
 *
 * The COVER is not here — it stays on `products.image_url`, which POS,
 * receipts, the QR menu and every order line's snapshot already read. This
 * endpoint holds only the additional gallery images.
 *
 * **Bytes never go through here.** The file is uploaded by the existing
 * `POST /uploads/file` flow with `context: 'product-image'`, which already does
 * magic-byte validation, recompression and the size ceiling; this records the
 * key that upload returned. One definition of an acceptable image, not two that
 * have to agree.
 */

export interface ProductImage {
  id: string;
  product_id: string;
  /** A fetchable URL on read — the API resolves the stored S3 key. */
  url: string;
  alt_text: string | null;
  sort_order: number;
  width: number | null;
  height: number | null;
  is_active: boolean;
}

/** Enforced by the API, in the service rather than a trigger — a COUNT(*)
 *  trigger is not concurrency-safe without a lock. Mirrored here so the UI can
 *  disable the picker rather than let an upload fail at the end. */
export const MAX_PRODUCT_IMAGES = 8;

export const productImageService = {
  list(productId: string) {
    return apiClient<ProductImage[]>(`/products/${productId}/images`);
  },

  /** `imageUrl` is the KEY returned by the upload, not a file. */
  add(productId: string, imageUrl: string, altText?: string) {
    return apiClient<ProductImage>(`/products/${productId}/images`, {
      method: 'POST',
      body: { image_url: imageUrl, ...(altText ? { alt_text: altText } : {}) },
    });
  },

  update(
    productId: string,
    imageId: string,
    payload: Partial<{ alt_text: string | null; sort_order: number; is_active: boolean }>,
  ) {
    return apiClient<ProductImage>(`/products/${productId}/images/${imageId}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  /** The whole order, every id, in the order they should appear. The API
   *  refuses a partial list rather than guessing where the rest go. */
  reorder(productId: string, imageIds: string[]) {
    return apiClient<ProductImage[]>(`/products/${productId}/images/order`, {
      method: 'PUT',
      body: { image_ids: imageIds },
    });
  },

  /** MANAGER, unlike the rest — it is the one action that loses something. */
  remove(productId: string, imageId: string) {
    return apiClient<{ removed: boolean }>(`/products/${productId}/images/${imageId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Merchandising: what the shop leads with.
 *
 * `is_bestseller` and `display_order` have existed on `products` with an index
 * since the menu work, and they drive the storefront's default `featured` sort
 * — with no UI anywhere, so a shopkeeper could not choose what their shop leads
 * with.
 *
 * The route is spelled `/menu/items/...` because that is where it was first
 * needed, but nothing on that path is gated on `business_type` and both columns
 * are vertical-neutral. Adding a second endpoint to change the same two columns
 * would be worse than the odd-looking URL.
 */
export const merchandisingService = {
  update(productId: string, payload: { is_bestseller?: boolean; display_order?: number }) {
    return apiClient<unknown>(`/menu/items/${productId}/presentation`, {
      method: 'PATCH',
      body: payload,
    });
  },
};
